import { useEffect, useState } from "react";
import api from "@services/axios/apiManager";
import { useOrder } from "@utils/store/cart/useOrder";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUser } from "@utils/store/user/useUser";
import { buildXContextHeader } from "@utils/api/headers";
import { isBeforeUseStart } from "@utils/func/couponPeriod";
import { isApplicableItemsSelectableInApp } from "@utils/func/couponScope";
import { triggerCouponLazyIssue } from "@utils/api/couponApi";
import { fetchKioskPreview, KioskPreview } from "./api/kioskApi";
import { useKiosk } from "./useKiosk";

/** 每張草稿只自動帶券一次（使用者清券後不再硬塞）。 */
const autoCouponFlag = (draftId: string) => `kiosk_autoCoupon_${draftId}`;

/**
 * Kiosk 結帳共用邏輯（購物車 + 確認頁共用）：
 * 1. 自動帶入「金額最高的 eligible 優惠券」（達低消、平台券或本瓦斯行帳戶券；每張草稿一次）。
 * 2. 依 draft / 券 / 品項變動重新 preview，並把 coupon_discount 寫進 store 的 couponDiscount，
 *    讓購物車與訂單摘要都能把折扣折在瓦斯價上（與應付總額一致）。
 */
export function useKioskCheckout(): { preview: KioskPreview | null; previewLoading: boolean } {
  const { draftOrderId, customerCouponId, setCustomerCouponId, pendingCouponId, setPendingCouponId, cartProductTotalPrice, productList, setCouponDiscount, setPreviewTotal } = useOrder();
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity } = useUser();
  const xContext = buildXContextHeader(chooseIdentity) || "";
  const { entrySupplierId } = useKiosk();
  // 無 CIS 新客戶沒有 primary_supplier → 與 useKioskBootstrap 同法退回「入口瓦斯行」，該行專屬券才過濾得到
  const supplierId = chooseIdentity?.primary_supplier?.supplier_id || entrySupplierId || undefined;

  const [preview, setPreview] = useState<KioskPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  // 自動帶券決策是否已完成（已帶券 or 確定無券）。決策完成前不讓 preview 把 couponDiscount 解析成 0，
  // 避免「preview 比 /coupons 先回 → 先顯示無券原價，auto-select 後才折扣」的閃動（race）。
  const [autoResolved, setAutoResolved] = useState(false);

  // 從優惠券頁「點券去下單」帶來的待套用券：優先於自動帶券，套用一次後清掉 pending。
  useEffect(() => {
    if (!pendingCouponId || !draftOrderId) return;
    setCustomerCouponId(pendingCouponId);
    setPendingCouponId(null);
  }, [pendingCouponId, draftOrderId, setCustomerCouponId, setPendingCouponId]);

  // 自動帶入最優券
  useEffect(() => {
    if (!draftOrderId || !cusJwtToken) return; // 尚未 ready，先別決策
    if (pendingCouponId) return; // 等上面把指定券套上，避免自動帶券搶先蓋掉
    if (cartProductTotalPrice <= 0) return; // 購物車金額還沒載入，無法判定資格
    if (customerCouponId) {
      setAutoResolved(true); // 已有券（草稿 / 使用者）→ 視為已決策
      return;
    }
    const flagKey = autoCouponFlag(draftOrderId);
    if (sessionStorage.getItem(flagKey)) {
      setAutoResolved(true); // 這張草稿之前已自動帶過 → 直接放行 preview
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        // 先觸發後端 lazy 補發限量券（使用平台事件），帶 X-Context 讓後端連該瓦斯行限量券一起補；
        // 再抓清單讓剛補發的券能被自動帶入
        await triggerCouponLazyIssue(cusJwtToken, xContext);
        if (cancelled) return;
        const res = await api.get("/app-api/coupons", {
          params: { status: "available", limit: 100, page: 1 },
          headers: { Authorization: `Bearer ${cusJwtToken}` },
        });
        if (cancelled) return;
        const items: Array<{
          customer_coupon_id: string;
          discount_value: number;
          min_order_amount: number;
          supplier_id: string | null;
          use_start_time: string | null;
          applicable_items: string | null;
        }> = res.data?.data?.items || [];
        // 不論有無符合的券都標記，避免每次 mount 重打 /coupons
        sessionStorage.setItem(flagKey, "1");
        const eligible = items.filter(
          (c) =>
            (c.min_order_amount ?? 0) <= cartProductTotalPrice &&
            !isBeforeUseStart(c.use_start_time) &&
            isApplicableItemsSelectableInApp(c.applicable_items) &&
            (!c.supplier_id || c.supplier_id === supplierId),
        );
        if (eligible.length > 0) {
          const best = eligible.reduce((a, b) => (b.discount_value > a.discount_value ? b : a));
          setCustomerCouponId(best.customer_coupon_id);
        }
      } catch (e) {
        console.error("kiosk auto coupon error", e);
      } finally {
        // 決策完成（不論有無帶券）才放行 preview，確保 preview 用的是最終的券
        if (!cancelled) setAutoResolved(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [draftOrderId, cusJwtToken, cartProductTotalPrice, customerCouponId, pendingCouponId, supplierId, setCustomerCouponId]);

  // 算費 + 同步折扣到 store（等自動帶券決策完成才跑，避免先閃無券原價）
  useEffect(() => {
    if (!draftOrderId || !cusJwtToken) {
      setPreviewLoading(false);
      return;
    }
    if (!autoResolved) return; // 決策未完成 → 維持 couponDiscount=null（pending），品項顯示 loading
    let cancelled = false;
    setPreviewLoading(true);
    (async () => {
      const p = await fetchKioskPreview({ draftId: draftOrderId, couponId: customerCouponId || null, jwtToken: cusJwtToken, xContext });
      if (cancelled) return;
      setPreview(p);
      setCouponDiscount(p?.coupon_discount ?? 0);
      // 券後應付金額 → 供點數折抵算上限（與訂購頁 GasOrderSummary 同一個基準）
      setPreviewTotal(p?.total ?? 0);
      setPreviewLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [draftOrderId, customerCouponId, JSON.stringify(productList), cusJwtToken, xContext, autoResolved]);

  return { preview, previewLoading };
}
